/**
 * Configurable performance budgets for the hero image.
 *
 * Budgets are expressed per device class (mobile / tablet / desktop) because a
 * 390px phone on 4G has very different headroom than a 1920px desktop. Each
 * `hero-perf` report is evaluated against the budget for the breakpoint it was
 * captured at, and any breach is:
 *   - logged with `console.warn("[hero-budget] …")`, and
 *   - dispatched as a `hero-perf-budget` CustomEvent so the on-page warning
 *     banner (and the /qa/hero/perf dashboard) can render it.
 *
 * Override order (last wins):
 *   1. DEFAULT_HERO_BUDGETS below
 *   2. localStorage key `hero-perf-budgets-v1` (set from /qa/hero/perf)
 *   3. URL params, e.g. `?heroBudgetLcp=2000&heroBudgetDecode=120`
 */

import type { HeroPerfReport } from "./hero-perf";
import { bucketBreakpoint, type Breakpoint } from "./hero-perf-samples";

export type BudgetMetric = "lcpMs" | "decodeMs" | "totalMs";

export type BudgetThresholds = {
  /** start → LCP paint budget (ms). */
  lcpMs: number;
  /** load → decoded budget (ms). */
  decodeMs: number;
  /** start → decoded budget (ms) — the "paintable" wall clock. */
  totalMs: number;
};

export type HeroBudgets = Record<Breakpoint, BudgetThresholds>;

export const BUDGETS_STORAGE_KEY = "hero-perf-budgets-v1";

/**
 * Defaults are tuned to the Core Web Vitals "good" LCP bar (2.5s) with margin,
 * since the hero image is the LCP element on every marketing page.
 */
export const DEFAULT_HERO_BUDGETS: HeroBudgets = {
  mobile: { lcpMs: 2500, decodeMs: 150, totalMs: 2000 },
  tablet: { lcpMs: 2200, decodeMs: 120, totalMs: 1800 },
  desktop: { lcpMs: 1800, decodeMs: 100, totalMs: 1400 },
};

export const METRIC_LABELS: Record<BudgetMetric, string> = {
  lcpMs: "LCP (start → largest paint)",
  decodeMs: "Decode (load → decoded)",
  totalMs: "Total (start → decoded)",
};

const isNum = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n) && n >= 0;

function mergeThresholds(
  base: BudgetThresholds,
  patch?: Partial<BudgetThresholds>,
): BudgetThresholds {
  if (!patch) return base;
  return {
    lcpMs: isNum(patch.lcpMs) ? patch.lcpMs : base.lcpMs,
    decodeMs: isNum(patch.decodeMs) ? patch.decodeMs : base.decodeMs,
    totalMs: isNum(patch.totalMs) ? patch.totalMs : base.totalMs,
  };
}

/** Reads stored overrides, then URL overrides, on top of the defaults. */
export function loadHeroBudgets(search?: string): HeroBudgets {
  let budgets: HeroBudgets = {
    mobile: { ...DEFAULT_HERO_BUDGETS.mobile },
    tablet: { ...DEFAULT_HERO_BUDGETS.tablet },
    desktop: { ...DEFAULT_HERO_BUDGETS.desktop },
  };

  if (typeof window !== "undefined") {
    try {
      const raw = localStorage.getItem(BUDGETS_STORAGE_KEY);
      const stored = raw ? (JSON.parse(raw) as Partial<HeroBudgets>) : null;
      if (stored) {
        budgets = {
          mobile: mergeThresholds(budgets.mobile, stored.mobile),
          tablet: mergeThresholds(budgets.tablet, stored.tablet),
          desktop: mergeThresholds(budgets.desktop, stored.desktop),
        };
      }
    } catch {
      /* corrupt / unavailable storage — fall back to defaults */
    }

    // URL overrides apply to every breakpoint (quick one-off experiments).
    const params = new URLSearchParams(
      search ?? window.location.search ?? "",
    );
    const patch: Partial<BudgetThresholds> = {};
    const num = (key: string) => {
      const v = params.get(key);
      if (v == null) return undefined;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };
    const lcp = num("heroBudgetLcp");
    const decode = num("heroBudgetDecode");
    const total = num("heroBudgetTotal");
    if (lcp != null) patch.lcpMs = lcp;
    if (decode != null) patch.decodeMs = decode;
    if (total != null) patch.totalMs = total;
    if (Object.keys(patch).length) {
      budgets = {
        mobile: mergeThresholds(budgets.mobile, patch),
        tablet: mergeThresholds(budgets.tablet, patch),
        desktop: mergeThresholds(budgets.desktop, patch),
      };
    }
  }

  return budgets;
}

export function saveHeroBudgets(budgets: HeroBudgets) {
  try {
    localStorage.setItem(BUDGETS_STORAGE_KEY, JSON.stringify(budgets));
  } catch {
    /* storage unavailable */
  }
}

export function resetHeroBudgets() {
  try {
    localStorage.removeItem(BUDGETS_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export type BudgetViolation = {
  metric: BudgetMetric;
  label: string;
  breakpoint: Breakpoint;
  actualMs: number;
  budgetMs: number;
  overByMs: number;
  overByPct: number;
};

export type BudgetEvaluation = {
  breakpoint: Breakpoint;
  viewportWidth: number;
  budgets: BudgetThresholds;
  measured: Partial<Record<BudgetMetric, number>>;
  violations: BudgetViolation[];
  currentSrc: string;
  at: string;
};

declare global {
  interface Window {
    __heroBudget?: BudgetEvaluation;
  }
}

/** Compares one hero-perf report against the active budgets. */
export function evaluateHeroBudget(
  report: HeroPerfReport,
  budgets: HeroBudgets = loadHeroBudgets(),
): BudgetEvaluation {
  const breakpoint = bucketBreakpoint(report.viewportWidth);
  const thresholds = budgets[breakpoint];
  const measured: Partial<Record<BudgetMetric, number>> = {
    lcpMs: report.deltas.lcpMs,
    decodeMs: report.deltas.decodeMs,
    totalMs: report.deltas.totalMs,
  };

  const violations: BudgetViolation[] = [];
  (Object.keys(METRIC_LABELS) as BudgetMetric[]).forEach((metric) => {
    const actual = measured[metric];
    const budget = thresholds[metric];
    // LCP is frequently absent (iframes, Safari <16) — skip rather than warn.
    if (!isNum(actual) || !isNum(budget) || actual <= budget) return;
    violations.push({
      metric,
      label: METRIC_LABELS[metric],
      breakpoint,
      actualMs: +actual.toFixed(1),
      budgetMs: budget,
      overByMs: +(actual - budget).toFixed(1),
      overByPct: +(((actual - budget) / budget) * 100).toFixed(0),
    });
  });

  return {
    breakpoint,
    viewportWidth: report.viewportWidth,
    budgets: thresholds,
    measured,
    violations,
    currentSrc: report.currentSrc,
    at: new Date().toISOString(),
  };
}

export const HERO_BUDGET_EVENT = "hero-perf-budget";

/**
 * Subscribes to `hero-perf` reports, evaluates them, warns in the console when
 * a budget is exceeded, and re-broadcasts the evaluation on
 * `hero-perf-budget`. Returns a disposer.
 */
export function startHeroBudgetMonitor(
  opts: { onEvaluate?: (evaluation: BudgetEvaluation) => void } = {},
): () => void {
  if (typeof window === "undefined") return () => {};

  const onPerf = (event: Event) => {
    const detail = (event as CustomEvent<HeroPerfReport>).detail;
    if (!detail || typeof detail.viewportWidth !== "number") return;

    const evaluation = evaluateHeroBudget(detail);
    window.__heroBudget = evaluation;

    if (evaluation.violations.length) {
      const summary = evaluation.violations
        .map(
          (v) =>
            `${v.label}: ${v.actualMs}ms vs ${v.budgetMs}ms budget (+${v.overByMs}ms / +${v.overByPct}%)`,
        )
        .join("\n  ");
      // eslint-disable-next-line no-console
      console.warn(
        `[hero-budget] Hero image exceeded the ${evaluation.breakpoint} budget at ${evaluation.viewportWidth}px:\n  ${summary}\n  src: ${evaluation.currentSrc}`,
      );
    }

    window.dispatchEvent(
      new CustomEvent(HERO_BUDGET_EVENT, { detail: evaluation }),
    );
    opts.onEvaluate?.(evaluation);
  };

  window.addEventListener("hero-perf", onPerf as EventListener);
  return () => window.removeEventListener("hero-perf", onPerf as EventListener);
}
